import { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('saas_token'));
  const [restrictedFeatures, setRestrictedFeatures] = useState([]);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setTenant(response.data.tenant || null);
      setIsSuperAdmin(response.data.is_super_admin || false);
      setRestrictedFeatures(response.data.user?.restricted_features || []);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, tenantSlug = null) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      tenant_slug: tenantSlug
    });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('saas_token', access_token);
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    setIsSuperAdmin(false);
    setRestrictedFeatures(userData?.restricted_features || []);
    return response.data;
  };

  const superAdminLogin = async (email, password) => {
    const response = await api.post('/auth/super-admin/login', {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('saas_token', access_token);
    setToken(access_token);
    setUser(userData);
    setTenant(null);
    setIsSuperAdmin(true);
    setRestrictedFeatures([]);
    return response.data;
  };

  const register = async (companyName, email, password, name) => {
    const response = await api.post('/auth/register', {
      company_name: companyName,
      email,
      password,
      name
    });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('saas_token', access_token);
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    setIsSuperAdmin(false);
    setRestrictedFeatures(userData?.restricted_features || []);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('saas_token');
    setToken(null);
    setUser(null);
    setTenant(null);
    setIsSuperAdmin(false);
    setRestrictedFeatures([]);
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Helper function to check if a feature is restricted for current user
  // Returns true if the feature is restricted (user does NOT have access)
  const isFeatureRestricted = (featureId) => {
    // Super admins have access to everything
    if (isSuperAdmin) return false;
    
    // Check if feature is in the global restricted list
    if (restrictedFeatures.includes(featureId)) return true;
    
    // If user has feature_access defined, check if they have access
    // Admins, owners, quality_auditors have access by default
    const adminRoles = ['admin', 'owner', 'quality_auditor', 'lead_quality_auditor'];
    if (adminRoles.includes(user?.role) || adminRoles.includes(user?.role_id) || user?.is_admin) {
      return false;
    }
    
    // For regular users, check their feature_access
    if (user?.feature_access) {
      // Convert featureId to match the key format (e.g., 'master_delete' -> 'master_delete')
      const accessKey = featureId.replace(/-/g, '_');
      return user.feature_access[accessKey] !== true;
    }
    
    // If no feature_access defined, restrict by default for sensitive features
    const sensitiveFeatures = ['billing', 'master_delete', 'audio_settings', 'onedrive'];
    return sensitiveFeatures.includes(featureId);
  };

  // Check if user has access to a specific feature (opposite of isFeatureRestricted)
  const hasFeatureAccess = (featureId) => {
    return !isFeatureRestricted(featureId);
  };

  // Check if user is a Quality Auditor (admin-level but with restrictions)
  const isQualityAuditor = user?.role_id === 'quality_auditor' || user?.role === 'quality_auditor';

  return (
    <AuthContext.Provider value={{
      user,
      tenant,
      isSuperAdmin,
      loading,
      token,
      login,
      superAdminLogin,
      register,
      logout,
      getAuthHeaders,
      isAuthenticated: !!token && !!user,
      restrictedFeatures,
      isFeatureRestricted,
      hasFeatureAccess,
      isQualityAuditor
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
